import { type FC, useState } from "react";

import { OptionalNote } from "../OptionalNote";

import { CourseGroupingDropdown } from "./CourseGroupingDropdown";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsActions } from "utils/routing";

type AddOrRenameCourseModalProps = {
  course?: CourseType;
  defaultGroupingId?: string | null;
  eventPrefix: string;
  onClose: () => void;
};

export const AddOrRenameCourseModal: FC<AddOrRenameCourseModalProps> = ({
  course,
  defaultGroupingId,
  eventPrefix,
  onClose,
}) => {
  const [code, setCode] = useState<string>(course?.code ?? "");
  const [title, setTitle] = useState<string>(course?.title ?? "");
  const [selectedGroupingId, setSelectedGroupingId] = useState<string | null>(
    defaultGroupingId || null,
  );
  const { setQueryParams } = useQueryParamsActions();

  const handleSubmit = async () => {
    if (!code.trim()) {
      return;
    }

    if (course) {
      await storeApi.courses.partial_update({
        id: course.id,
        code: code.trim(),
        title: title.trim(),
      });
      toastSuccessMessage(t("home.toasts.course_updated"));
    } else {
      const newCourse = await storeApi.courses.create({
        grouping_id: selectedGroupingId,
        code: code.trim(),
        title: title.trim(),
      });
      toastSuccessMessage(t("home.toasts.course_added"));
      setQueryParams({ [COURSE_ID_QUERY_PARAM]: newCourse.id });
    }

    onClose();
  };

  return (
    <div>
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.courses.course_code_label")}</div>
          <TextField
            eventName={`${eventPrefix} / Edit Code`}
            placeholder={t("home.placeholder.course_code")}
            value={code}
            isEditing
            onChange={(e) => setCode(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>

        <label className="flex flex-col gap-1">
          <div className="flex items-center justify-between font-medium">
            {t("glossary.course_title")}
            <OptionalNote />
          </div>
          <TextField
            eventName={`${eventPrefix} / Edit Title`}
            placeholder={t("home.placeholder.course_title")}
            value={title}
            isEditing
            onChange={(e) => setTitle(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>

        {!course && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between font-medium">
              {t("home.courses.add_course_to_grouping_label")}
              <OptionalNote />
            </div>

            <CourseGroupingDropdown
              selectedGroupingId={selectedGroupingId}
              onSelectionChange={setSelectedGroupingId}
            />
          </div>
        )}
      </div>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={!code.trim()}
          kind="accent"
          size="m"
          title={course ? t("common.save_changes") : t("common.add.course")}
          onClick={handleSubmit}
        >
          {course ? t("common.save_changes") : t("common.add.course")}
        </SquareButton>
      </div>
    </div>
  );
};
